import React from "react";
import { useMultipleSelection, useCombobox } from "downshift";
import { useStore } from "../../stores/store";
import cx from "classnames";
import useMask from "./useParcelInputMask";
import debounce from "lodash/debounce";
import InputMask from "react-input-mask";

interface AutoCompleteProps {
  getOptions: (value: string) => Promise<any>;
  onSearch: (value: string) => void;
  placeholder?: string;
  searchLabel: string;
  parcelItems: Option[];
  setParcelItems: (items: Option[]) => void;
}

interface Option {
  label: string;
  value: string;
}

const inputStyle = {
  minHeight: "22px",
};

const inputClasses =
  "bg-white text-sm px-3 py-[1px] rounded-xl w-full text-black";

export default function ParcelAutoComplete(props: AutoCompleteProps) {
  const [loading, setLoading] = React.useState(false);
  const { searchValue, setSearchValue, selectedParcelItems, setSelectedParcelItems } = useStore();
  const { onInput, onKeyDown } = useMask();
  const { getSelectedItemProps, getDropdownProps, removeSelectedItem } =
    useMultipleSelection({
      selectedItems: selectedParcelItems,
      onStateChange({ selectedItems: newSelectedItems, type }) {
        switch (type) {
          case useMultipleSelection.stateChangeTypes
            .SelectedItemKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.SelectedItemKeyDownDelete:
          case useMultipleSelection.stateChangeTypes.DropdownKeyDownBackspace:
          case useMultipleSelection.stateChangeTypes.FunctionRemoveSelectedItem:
            setSelectedParcelItems(newSelectedItems || []);
            break;
          default:
            break;
        }
      },
    });
  const {
    isOpen,
    getToggleButtonProps,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getItemProps,
    selectedItem,
  } = useCombobox({
    items: props.parcelItems,
    itemToString(item) {
      return item ? item.label : "";
    },
    defaultHighlightedIndex: 0, // after selection, highlight the first item.
    selectedItem: null,
    inputValue: searchValue,
    stateReducer(state, actionAndChanges) {
      const { changes, type } = actionAndChanges;

      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
          return {
            ...changes,
            isOpen: true, // keep the menu open after selection.
            highlightedIndex: 0, // with the first option highlighted.
          };
        default:
          return changes;
      }
    },
    onStateChange({
      inputValue: newInputValue,
      type,
      selectedItem: newSelectedItem,
    }) {
      switch (type) {
        case useCombobox.stateChangeTypes.InputKeyDownEnter:
        case useCombobox.stateChangeTypes.ItemClick:
        case useCombobox.stateChangeTypes.InputBlur:
          if (newSelectedItem) {
            setSelectedParcelItems([
              ...selectedParcelItems,
              newSelectedItem,
            ]);
            setSearchValue("");
          }
          break;

        case useCombobox.stateChangeTypes.InputChange:
          onInputValueChange(newInputValue || "");
          break;
        default:
          break;
      }
    },
  });

  const placeholder = props.placeholder || "Search...";

  const loadOptions = async (value: string) => {
    if (value) {
      setLoading(true);
      const options = await props.getOptions(value);
      props.setParcelItems(options as any as Option[]);
      setLoading(false);
    } else {
      props.setParcelItems([]);
      setSearchValue("");
    }
  };

  const debouncedLoadOptions = debounce(loadOptions, 15);

  const onInputValueChange = (inputValue: string) => {
    if (inputValue) {
      setLoading(true);
      setSearchValue(inputValue);
      debouncedLoadOptions(inputValue);
    } else {
      props.setParcelItems([]);
      setSearchValue("");
    }
  };

  return (
    <div className="w-full">
      <div className="flex flex-col gap-1">
        <div className="rounded-md shadow-sm bg-white inline-flex gap-2 items-center flex-wrap text-black">
          {selectedParcelItems.map(function renderSelectedItem(
            selectedItemForRender,
            index
          ) {
            return (
              <span
                className="bg-gray-100 rounded-md px-1 focus:bg-red-400 text-xs"
                key={`selected-item-${index}`}
                {...getSelectedItemProps({
                  selectedItem: selectedItemForRender,
                  index,
                })}
              >
                {selectedItemForRender.label}
                <span
                  className="px-1 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeSelectedItem(selectedItemForRender);
                  }}
                >
                  &#10005;
                </span>
              </span>
            );
          })}
          <div className="flex gap-0.5 grow">
            <input
              placeholder={selectedParcelItems.length ? "Add Parcel" : placeholder}
              className={inputClasses}
              {...getInputProps(getDropdownProps({ preventKeyAction: isOpen }))}
              onKeyDown={onKeyDown}
              onInput={onInput}
            />
          </div>
        </div>
      </div>
      <ul
        className={`absolute text-sm py-1 w-4/5 bg-white text-black rounded-md z-50 max-h-72 md:w-[420px] overflow-auto shadow-xl border border-gray-200 ${(!isOpen || searchValue === "") && "hidden"
          } ${loading && "opacity-50"}`}
        {...getMenuProps()}
      >
        {isOpen ? (
          <>
            {props.parcelItems?.length > 0 &&
              props.parcelItems
                .filter(
                  (item) =>
                    !searchValue ||
                    item.label
                      .toLowerCase()
                      .includes(searchValue.toLowerCase())
                )
                .map((item, index) => {
                  const matchIndex = item.label
                    .toLowerCase()
                    .indexOf(searchValue.toLowerCase());
                  const matchLength = searchValue.length;
                  const before = item.label.slice(0, matchIndex);
                  const match = item.label.slice(
                    matchIndex,
                    matchIndex + matchLength
                  );
                  const after = item.label.slice(matchIndex + matchLength);

                  return (
                    <li
                      className={`${selectedItem === item && "font-bold"
                        } text-left w-auto px-1 py-0.5 hover:bg-gray-600 hover:text-white hover:underline cursor-pointer`}
                      key={`${item.value}${index}`}
                      {...getItemProps({
                        item,
                        index,
                      })}
                    >
                      <span>{before}</span>
                      <b className="bg-yellow-200 text-black">{match}</b>
                      <span>{after}</span>
                    </li>
                  );
                })}
            {props.parcelItems?.length === 0 &&
              !loading &&
              searchValue !== "" ? (
              <li className="text-left w-auto px-1 py-0.5">No results found</li>
            ) : null}
          </>
        ) : null}
      </ul>
    </div>
  );
}
